/**
 * Studio FTUX constants shared across the stack. These live in `src/constants`
 * (rather than a frontend feature module) because the Express session
 * middleware imports them server-side; keeping them free of browser-only
 * dependencies means importing here can never break server startup.
 *
 * A Studio "test as a client" preview magic link carries `studioClientPreview=true`
 * as its post-login destination param. The session middleware reads it to skip the
 * single-use confirmation interstitial for these freshly minted preview links.
 */
export const STUDIO_CLIENT_PREVIEW_PARAM = 'studioClientPreview';
export const STUDIO_CLIENT_PREVIEW_VALUE = 'true';

// The preview link carries the client-side "Getting started" progress bar
// percentage, because the client session can't read the studio owner's
// onboarding-task-status record itself. It reflects whether the owner has
// published their first app: full bar when done, half otherwise.
export const STUDIO_CLIENT_PREVIEW_TASK_PROGRESS_PARAM = 'taskProgress';
export const STUDIO_CLIENT_PREVIEW_PROGRESS_COMPLETE = 100;
export const STUDIO_CLIENT_PREVIEW_PROGRESS_IN_PROGRESS = 50;

/**
 * PostHog workspace group property that puts a portal in the Studio cohort.
 * Must match backend `segmentcli.StudioWorkspaceGroupTrait` / Value (`"studio"` /
 * `"true"`). SSR flag evaluation passes this via `groupProperties` so Studio
 * flags resolve on first paint before `$groupidentify` is ingested.
 */
export const STUDIO_WORKSPACE_GROUP_PROPERTY = 'studio';
export const STUDIO_WORKSPACE_GROUP_PROPERTY_VALUE = 'true';

/**
 * Narrower cohort, set only for workspaces onboarded through the studio-pricing
 * entry. The `studio-pricing` flag targets this trait so it can be enabled per
 * test workspace rather than for every Studio workspace.
 */
export const STUDIO_PRICING_WORKSPACE_GROUP_PROPERTY = 'studioPricing';
export const STUDIO_PRICING_WORKSPACE_GROUP_PROPERTY_VALUE = 'true';

/**
 * The Studio onboarding entry a signup came through, and the value persisted as
 * `PortalConfig.referrer` (mirrors backend `portalconfig.StudioReferrer`). The
 * pricing entry is a superset of `studio`: same funnel and emails, plus the
 * pricing cohort.
 *
 * These live here rather than with the onboarding route constants because the
 * Express server reads the persisted value during SSR flag evaluation, and this
 * module is guaranteed free of browser-only dependencies.
 */
export const STUDIO_REFERRER = 'studio';
export const STUDIO_PRICING_REFERRER = 'studio-pricing';

/** Never compare a referrer to one value — the pricing entry is also Studio. */
export const isStudioPricingReferrer = (referrer?: string): boolean =>
  referrer === STUDIO_PRICING_REFERRER;

/**
 * The CRM page stamps this `window.name` on the Internal Portal tab before it
 * opens a "test as a client" preview tab. From the preview, the client-portal
 * "Return to CRM" button re-targets this name with `window.open('', name)` to
 * switch the browser back to that existing Internal Portal tab — `opener.focus()`
 * is blocked by browsers as focus-stealing, but targeting an existing named tab
 * on a user click is honored.
 */
export const STUDIO_INTERNAL_PORTAL_TAB_NAME = 'assemblyInternalPortal';
